import React, { useState, useEffect } from 'react';
import { Text, RichText } from '@sitecore-jss/sitecore-jss-react';
import { AttachmentsView } from 'Components/Common/AttachmentsView';
import { Button } from 'Components/Common/Button';
import FormDropdown from 'Components/Inputs/FormDropdown';
import FormInputWrapper from 'Components/Inputs/FormInputWrapper';
import FormRadioGroup from 'Components/Inputs/FormRadioGroup';
import FormTextArea from 'Components/Inputs/FormTextArea';
import { useHBTFormContext } from 'Feature/Claims/components/HBTFormContext';
import i18n from 'i18next';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { FormProvider } from 'react-hook-form';
import { config } from '../../../../../../config';
import styles from './styles.module.scss';
import { AllowedFileTypes, DeclineClaimProps } from './types';
import useDeclineClaimFunctions from './useDeclineClaim';

export const DeclineClaimForm: React.FC<DeclineClaimProps> = ({
  onCancelCallback,
  onSuccessCallback,
  onErrorCallback,
  fields
}: DeclineClaimProps) => {
  const { claimData, loadingStatus } = useHBTFormContext();
  const { hookForm, handleSubmit, onSubmitErrors, displayedRequestTemplate, cancelFlyout } =
    useDeclineClaimFunctions(
      fields,
      onSuccessCallback,
      onErrorCallback,
      onCancelCallback,
      claimData?.detail?.claimTypeCode,
      claimData?.cmhcLoanAccountNumber
    );
  const watchDeclineReason = hookForm.watch('declineReasonCode');
  const [declineReasonOther, setDeclineReasonOther] = useState(false);

  useEffect(() => {
    watchDeclineReason == 9 ? setDeclineReasonOther(true) : setDeclineReasonOther(false);
  }, [watchDeclineReason]);

  return (
    <Scrollbars>
      <div className={styles.declineClaimFormHeader} data-testid={`declineClaimForm`}>
        <div className={`${styles.textHeader}`}>
          <Text tag="h2" field={fields.formTitle} />
        </div>
        <div className={styles.closeButton}>
          <button
            type="button"
            data-testid="addDclineClaimCloseButton"
            onClick={cancelFlyout}
            aria-label={i18n.t('Accessibility-Close-Button') ?? ''}
          >
            <span className="material-icons align-self-center">close</span>
          </button>
        </div>
      </div>
      <div className={`${styles.declineClaimFormBody}`}>
        <div className={`${styles.textStyle}`}>
          <Text tag="p" field={fields.content} />
        </div>
        <FormProvider {...hookForm}>
          <form
            className={styles.declineForm}
            onSubmit={hookForm.handleSubmit(handleSubmit, onSubmitErrors)}
            id="declineClaimForm"
          >
            <div className={styles.templateWrapper}>
              <div className={styles.languageCodeWrapper}>
                <FormRadioGroup
                  name="languageCode"
                  className=""
                  label={fields.languageLabel}
                  radioLabels={fields.languageOptions}
                  defaultSelection={claimData?.lender?.correspondenceLanguageCode}
                  setAsNumber={true}
                />
              </div>
              <FormDropdown
                name="declineReasonCode"
                label={fields.categoryLabel}
                disableDefaultSelect={false}
                options={fields.categoryList?.fields?.listItems}
              />

              {declineReasonOther && (
                <div className={styles.templateWrapper}>
                  <FormTextArea
                    name="otherDeclineReasonDescription"
                    className={styles.templateTextArea}
                    label={fields.otherDescription}
                    textAreaHelperText={fields.characterLimit.value}
                    charLimit={parseInt(fields.limitNumber.value ?? '0', 10)}
                  />
                </div>
              )}

              <div className={styles.notificationTemplate}>
                <FormTextArea
                  name="remarkText"
                  label={fields.note}
                  className={styles.declineNotificationTemplate}
                  placeholder={fields.notePlaceholder.value}
                  charLimit={Number(fields.limitNumber.value)}
                  textAreaHelperText={fields.characterLimit.value}
                />
                <FormInputWrapper
                  name="notificationTemplateLabel"
                  label={fields.notificationTemplateLabel}
                />
                <RichText
                  field={displayedRequestTemplate.field}
                  className={styles.notificationTemplateWrapper}
                  name="declineNotificationTemplate"
                />
              </div>
            </div>
            {React.createElement(AttachmentsView, {
              isOptional: true,
              registeredVal: 'attachments',
              fields: fields.attachmentsView.fields,
              endpoint: config.claimApi.urls.upload,
              allowedFileTypes: AllowedFileTypes
            })}

            <div className={`${styles.twoColumns} ${styles.formButtonsWrapper}`}>
              <div>
                <Button
                  name="submitButton"
                  responsive
                  loading
                  type="submit"
                  ariaText={{ value: 'Submit' }}
                  text={fields.submitBtn}
                  disabled={loadingStatus?.isLoading && loadingStatus?.isFlyout}
                  icon={
                    loadingStatus?.isLoading && loadingStatus?.isFlyout
                      ? () => (
                          <span className={styles.saveIconWrapper}>
                            <span className={`material-icons align-self-center`}>loop</span>
                          </span>
                        )
                      : undefined
                  }
                />
              </div>
              <div>
                <Button
                  secondaryButton
                  responsive
                  onClick={cancelFlyout}
                  ariaText={{}}
                  name={`CancelButton`}
                  text={fields.cancelBtn}
                  type="button"
                  disabled={loadingStatus?.isLoading && loadingStatus?.isFlyout}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </Scrollbars>
  );
};
